/**
 * `/admin` 계정 관리 — §10.0.
 *
 * 관리자가 여기서 하는 일은 두 가지 결정이다:
 *   1. **로그인해도 되는가** — 가입 승인 / 중지 / 재개 / 삭제.
 *   2. **자원을 써도 되는가** — 방 개설 권한(GPU 를 태우는 조작).
 * 둘은 다른 축이다. 듣기만 하는 참가자에게 승인은 주되 개설 권한은 주지
 * 않는 조합이 실사용에서 가장 흔하다.
 *
 * 이 화면은 통제가 **아니다**. 서버의 `_require_admin` 이 모든 관리 조작의
 * 단일 관문이고, 여기서 버튼을 숨기는 것은 관계없는 사람에게 쓸모없는
 * 버튼을 안 보여 주는 편의일 뿐이다. 비관리자가 이 주소로 직접 들어오면
 * 목록 요청이 403 으로 떨어지고 화면은 그 사실을 말한다.
 *
 * 되돌릴 수 없는 조작(삭제·중지·관리 권한 해제)은 **확인을 받는다**.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { Button } from '@cupia/ui/button'
import { Badge } from '@cupia/ui/badge'
import { Card, CardContent } from '@cupia/ui/card'
import { Input } from '@cupia/ui/input'
import { Label } from '@cupia/ui/label'
import { Switch } from '@cupia/ui/switch'
import { Spinner } from '@cupia/ui/spinner'
import { Empty } from '@cupia/ui/empty'
import { Alert, AlertDescription, AlertTitle } from '@cupia/ui/alert'
import { confirm } from '@cupia/ui/confirm-dialog'
import {
  ArrowLeft,
  KeyRound,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  UserPlus,
} from 'lucide-react'
import { Brand } from '../components/brand'
import { RoomAdminPanel } from '../components/room-admin-panel'
import { MeetingsAdminPanel } from '../components/meetings-admin-panel'
import { UpstreamPanel } from '../components/upstream-panel'
import { RoomHistoryPanel } from '../components/room-history-panel'
import { ThemeControls } from '../components/theme-controls'
import { WhatsNew } from '../components/whats-new'
import { ApiError } from '../lib/api'
import {
  STATUS_LABELS,
  approveAccount,
  createAccount,
  deleteAccount,
  formatStamp,
  listAccounts,
  setAccountEnabled,
  setAdmin,
  setCanCreateRoom,
  type AdminAccount,
} from '../lib/admin'

export function AdminRoute(): React.ReactElement {
  const [accounts, setAccounts] = useState<AdminAccount[] | null>(null)
  const [denied, setDenied] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const navigate = useNavigate()

  const notify = useCallback((message: string) => {
    setToast(message)
    setTimeout(() => setToast(null), 3200)
  }, [])

  const reload = useCallback(async () => {
    try {
      const res = await listAccounts()
      setAccounts(res.accounts)
      setDenied(false)
    } catch (e) {
      // 권한이 없으면 목록 자체가 오지 않는다 — 화면은 그 사실을 말한다.
      if (e instanceof ApiError && (e.status === 403 || e.status === 401)) {
        setDenied(true)
        setAccounts([])
        return
      }
      notify('계정 목록을 불러오지 못했습니다.')
    }
  }, [notify])

  useEffect(() => {
    void reload()
  }, [reload])

  /** 조작 한 번 = 요청 + 목록 갱신 + 결과 알림. 조용히 끝내지 않는다. */
  const act = useCallback(
    async (fn: () => Promise<unknown>, success: string) => {
      try {
        await fn()
        await reload()
        notify(success)
      } catch (e) {
        notify(e instanceof ApiError ? e.message : '요청이 실패했습니다.')
      }
    },
    [reload, notify],
  )

  const onToggleAdmin = useCallback(
    async (a: AdminAccount) => {
      if (a.is_admin) {
        // 관리 권한 해제는 되돌리기 쉬우나, 자기 자신을 해제하면 이 화면을
        // 잃는다 — 무엇을 잃는지 말하고 확인을 받는다.
        const ok = await confirm({
          title: `${a.username} 의 관리자 권한을 해제할까요?`,
          description:
            '해제하면 계정 관리 화면에 들어올 수 없습니다. 마지막 관리자는 서버가 해제를 거부합니다.',
          confirmLabel: '해제',
          cancelLabel: '취소',
          variant: 'destructive',
        })
        if (!ok) return
      }
      await act(
        () => setAdmin(a.username, !a.is_admin),
        a.is_admin ? '관리자 권한을 해제했습니다.' : '관리자로 지정했습니다.',
      )
    },
    [act],
  )

  const onToggleEnabled = useCallback(
    async (a: AdminAccount) => {
      const disabling = a.status !== 'disabled'
      if (disabling) {
        const ok = await confirm({
          title: `${a.username} 계정을 중지할까요?`,
          description:
            '접속 중인 세션이 즉시 끊기고 다시 로그인할 수 없습니다. 나중에 다시 사용할 수 있습니다.',
          confirmLabel: '중지',
          cancelLabel: '취소',
          variant: 'destructive',
        })
        if (!ok) return
      }
      await act(
        () => setAccountEnabled(a.username, !disabling),
        disabling ? '계정을 중지했습니다.' : '계정을 다시 사용하도록 했습니다.',
      )
    },
    [act],
  )

  const onDelete = useCallback(
    async (a: AdminAccount) => {
      const ok = await confirm({
        title: `${a.username} 계정을 삭제할까요?`,
        description:
          '되돌릴 수 없습니다. 계정과 세션이 즉시 사라집니다. 잠시 막아 두려는 것이라면 "중지" 를 쓰세요.',
        confirmLabel: '삭제',
        cancelLabel: '취소',
        variant: 'destructive',
      })
      if (!ok) return
      await act(() => deleteAccount(a.username), '계정을 삭제했습니다.')
    },
    [act],
  )

  if (accounts === null) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-3 text-[var(--color-muted-foreground)]">
        <Spinner className="h-4 w-4" />
        계정 목록을 불러오는 중…
      </div>
    )
  }

  if (denied) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <Empty
          icon={<KeyRound className="h-6 w-6" />}
          title="관리자만 볼 수 있습니다"
          description="관리자 계정으로 로그인한 뒤 다시 시도해 주세요."
          action={
            <Button asChild>
              <Link to="/">처음으로</Link>
            </Button>
          }
        />
      </div>
    )
  }

  const pending = accounts.filter((a) => a.status === 'pending')

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <header className="border-b border-[var(--color-border)]">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-4">
          <Brand subtitle="계정 관리" />
          <div className="ms-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => void navigate('/')}>
              <ArrowLeft className="h-3.5 w-3.5" />
              회의로 돌아가기
            </Button>
            <ThemeControls />
          </div>
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl flex-col gap-6 px-6 py-8">
        {/* 승인 대기가 있으면 가장 먼저 말한다 — 관리자가 이 화면에 오는
            가장 흔한 이유다. */}
        {pending.length > 0 && (
          <Alert data-testid="admin-pending-alert">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>승인을 기다리는 계정 {pending.length}개</AlertTitle>
            <AlertDescription>
              승인하기 전까지 이 계정들은 로그인할 수 없습니다.
            </AlertDescription>
          </Alert>
        )}

        {/* §9.2 회의실 관리 — 계정보다 위에 둔다. 떠 있는 방은 지금 자원을
            쓰고 있고, 관리자가 이 화면에 오는 이유가 대개 그것이다. */}
        <RoomAdminPanel onNotify={notify} />
        <MeetingsAdminPanel onNotify={notify} />
        <UpstreamPanel onNotify={notify} />
        <RoomHistoryPanel isAdmin />

        <NewAccountPanel
          onCreated={(name) => {
            void reload()
            notify(`${name} 계정을 만들었습니다.`)
          }}
          onError={notify}
        />

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-start text-(length:--text-sm-size)" data-testid="admin-accounts">
                <thead className="border-b border-[var(--color-border)] text-[var(--color-muted-foreground)]">
                  <tr>
                    <th className="px-4 py-2.5 text-start font-medium">아이디</th>
                    <th className="px-4 py-2.5 text-start font-medium">상태</th>
                    <th className="px-4 py-2.5 text-start font-medium">방 개설</th>
                    <th className="px-4 py-2.5 text-start font-medium">관리자</th>
                    <th className="px-4 py-2.5 text-start font-medium">마지막 로그인</th>
                    <th className="px-4 py-2.5 text-end font-medium">조치</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr
                      key={a.username}
                      data-testid={`admin-row-${a.username}`}
                      className="border-b border-[var(--color-border)] last:border-0"
                    >
                      <td className="px-4 py-2.5 font-medium">{a.username}</td>
                      <td className="px-4 py-2.5">
                        <Badge
                          variant={
                            a.status === 'approved'
                              ? 'softSuccess'
                              : a.status === 'pending'
                                ? 'softGray'
                                : 'destructive'
                          }
                        >
                          {STATUS_LABELS[a.status]}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        {/* §10.0 자원 통제 — 승인과 다른 축이다 */}
                        <Switch
                          checked={a.can_create_room}
                          aria-label={`${a.username} 방 개설 권한`}
                          data-testid={`admin-cap-${a.username}`}
                          onCheckedChange={(next: boolean) =>
                            void act(
                              () => setCanCreateRoom(a.username, next),
                              next
                                ? '방 개설 권한을 부여했습니다.'
                                : '방 개설 권한을 회수했습니다.',
                            )
                          }
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <Button
                          variant={a.is_admin ? 'secondary' : 'ghost'}
                          size="sm"
                          data-testid={`admin-role-${a.username}`}
                          onClick={() => void onToggleAdmin(a)}
                        >
                          <ShieldCheck className="h-3.5 w-3.5" />
                          {a.is_admin ? '관리자' : '일반'}
                        </Button>
                      </td>
                      <td className="px-4 py-2.5 text-[var(--color-muted-foreground)]">
                        {formatStamp(a.last_login_at)}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {a.status === 'pending' && (
                            <Button
                              size="sm"
                              data-testid={`admin-approve-${a.username}`}
                              onClick={() =>
                                void act(
                                  () => approveAccount(a.username),
                                  '계정을 승인했습니다.',
                                )
                              }
                            >
                              승인
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`admin-enabled-${a.username}`}
                            onClick={() => void onToggleEnabled(a)}
                          >
                            {a.status === 'disabled' ? '다시 사용' : '중지'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`${a.username} 삭제`}
                            data-testid={`admin-delete-${a.username}`}
                            onClick={() => void onDelete(a)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* §whats-new 개선사항 알림 — 이 return 은 **관리자만** 닿는다(denied·
          로딩은 위에서 먼저 반환됐다). 그래서 여기가 "관리자가 방금 왔다" 를
          모호함 없이 정하는 지점이다. 한 번만 뜨는 규칙은 컴포넌트가 든다. */}
      <WhatsNew />

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-[var(--radius-lg)] bg-[var(--color-foreground)] px-4 py-2 text-(length:--text-sm-size) text-[var(--color-background)] shadow-lg"
        >
          {toast}
        </div>
      )}
    </div>
  )
}

/**
 * 계정 직접 생성 — 가입 대기 없이 만든다.
 *
 * 회의 30분 전에 참가자가 정해지는 것이 실사용이다. 그때 "가입하고 승인을
 * 기다리라" 고 하면 회의가 먼저 시작된다.
 */
function NewAccountPanel({
  onCreated,
  onError,
}: {
  onCreated: (username: string) => void
  onError: (message: string) => void
}): React.ReactElement {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [canCreateRoom, setCanCreate] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [busy, setBusy] = useState(false)

  const submit = useCallback(async () => {
    if (busy || !username.trim() || !password) return
    setBusy(true)
    try {
      const res = await createAccount({
        username: username.trim().toLowerCase(),
        password,
        // 관리자가 직접 만든 계정은 **승인된 상태**로 태어난다 — 만든 사람이
        // 곧 승인 권한자인데 다시 승인을 요구하면 단계만 늘어난다.
        approved: true,
        is_admin: isAdmin,
        can_create_room: canCreateRoom,
      })
      setUsername('')
      setPassword('')
      setIsAdmin(false)
      setCanCreate(true)
      onCreated(res.username)
    } catch (e) {
      onError(e instanceof ApiError ? e.message : '계정을 만들지 못했습니다.')
    } finally {
      setBusy(false)
    }
  }, [busy, username, password, isAdmin, canCreateRoom, onCreated, onError])

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4">
        <div className="flex items-center gap-2 font-medium">
          <UserPlus className="h-4 w-4" />
          계정 직접 만들기
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="admin-new-username">아이디</Label>
            <Input
              id="admin-new-username"
              data-testid="admin-new-username"
              value={username}
              autoComplete="off"
              placeholder="영문 소문자로 시작하는 3자 이상"
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="admin-new-password">비밀번호</Label>
            <Input
              id="admin-new-password"
              data-testid="admin-new-password"
              type="password"
              value={password}
              autoComplete="new-password"
              placeholder="8자 이상"
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2 text-(length:--text-sm-size)">
            <Switch
              id="admin-new-can-create"
              checked={canCreateRoom}
              data-testid="admin-new-can-create"
              onCheckedChange={setCanCreate}
            />
            <Label htmlFor="admin-new-can-create">방 개설 권한</Label>
          </div>
          <div className="flex items-center gap-2 text-(length:--text-sm-size)">
            <Switch
              id="admin-new-is-admin"
              checked={isAdmin}
              data-testid="admin-new-is-admin"
              onCheckedChange={setIsAdmin}
            />
            <Label htmlFor="admin-new-is-admin">관리자</Label>
          </div>
          <Button
            className="ms-auto"
            disabled={busy || !username.trim() || !password}
            data-testid="admin-new-submit"
            onClick={() => void submit()}
          >
            <UserPlus className="h-4 w-4" />
            만들기
          </Button>
        </div>
        <p className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
          만든 계정은 바로 로그인할 수 있습니다. 비밀번호 규칙은 가입과 같습니다.
        </p>
      </CardContent>
    </Card>
  )
}
